// React
import { useMemo, useState } from 'react';

// Mantine
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Group,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  Timeline,
} from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import {
  IconAlertTriangle,
  IconFile,
  IconPaperclip,
  IconSend,
  IconUpload,
  IconX,
} from '@tabler/icons-react';

// Interfaces
import { ActionRequest, ActionResponse, ResponseAttachment } from 'interfaces';
import { Chain } from './ViewClaimActionsModal';

// Enum
import { ActionRequestStatus, ActionRequestType } from '@billing/validation';

// Components
import { RequestCard } from './RequestCard';
import { ResponseCard } from './ResponseCard';

export const ChainTimeline = ({
  chain,
  attachmentsByResponseID,
  attachmentLoading,
  onSatisfyResponse,
  onCancelResponse,
  openRejectDialog,
  isProviderMode = false,
  onSubmitProviderResponse,
  statusColor,
  typeColor,
  typeLabel,
}: {
  chain: Chain;
  attachmentsByResponseID: Record<string, ResponseAttachment[]>;
  isProviderMode?: boolean;
  onSubmitProviderResponse?: ({
    request,
    message,
    files,
  }: {
    request: ActionRequest;
    message: string;
    files: FileWithPath[];
  }) => Promise<void>;
  onSatisfyResponse: (
    request: ActionRequest,
    response: ActionResponse
  ) => Promise<void>;
  onCancelResponse: (
    request: ActionRequest,
    response: ActionResponse
  ) => Promise<void>;
  openRejectDialog: (request: ActionRequest, response: ActionResponse) => void;
  attachmentLoading: boolean;
  statusColor: Record<ActionRequestStatus, string>;
  typeColor: Record<ActionRequestType, string>;
  typeLabel: string;
}) => {
  const events = useMemo(() => {
    const out: Array<
      | { kind: 'request'; req: ActionRequest }
      | { kind: 'response'; req: ActionRequest; res: ActionResponse }
    > = [];

    chain.steps.forEach((s) => {
      out.push({ kind: 'request', req: s.request });
      if (s.response)
        out.push({ kind: 'response', req: s.request, res: s.response });
    });

    return out;
  }, [chain]);

  const latestStep = chain.steps[chain.steps.length - 1];
  const showProviderResponseForm =
    isProviderMode &&
    !!latestStep &&
    latestStep.request.status === ActionRequestStatus.REQUESTED &&
    !latestStep.response &&
    !!onSubmitProviderResponse;

  return (
    <ScrollArea h={390} type="auto" pr="sm" data-testid="chain-timeline">
      <Stack gap="md">
        <Timeline bulletSize={10} lineWidth={2}>
          {events.map((ev) => {
            if (ev.kind === 'request') {
              return (
                <Timeline.Item
                  key={`req-${ev.req.id}`}
                  bullet={
                    <StatusBullet
                      status={ev.req.status}
                      statusColor={statusColor}
                    />
                  }
                >
                  <RequestCard
                    request={ev.req}
                    statusColor={statusColor}
                    typeColor={typeColor}
                    typeLabel={typeLabel}
                  />
                </Timeline.Item>
              );
            }

            const attachments = attachmentsByResponseID[ev.res.id] ?? [];

            return (
              <Timeline.Item
                key={`res-${ev.res.id}`}
                bullet={
                  <StatusBullet
                    status={ev.req.status}
                    statusColor={statusColor}
                  />
                }
              >
                <ResponseCard
                  request={ev.req}
                  response={ev.res}
                  attachments={attachments}
                  attachmentLoading={attachmentLoading}
                  typeLabel={typeLabel}
                  typeColor={typeColor}
                  onSatisfyResponse={onSatisfyResponse}
                  onCancelResponse={onCancelResponse}
                  openRejectDialog={openRejectDialog}
                  canResolve={!isProviderMode}
                  statusColor={statusColor}
                />
              </Timeline.Item>
            );
          })}
        </Timeline>

        {chain.steps.length > 0 &&
          !chain.steps[chain.steps.length - 1].response && (
            <Text size="sm" c="dimmed" data-testid="chain-timeline-empty">
              {'No response yet.'}
            </Text>
          )}
        {showProviderResponseForm && (
          <ProviderResponseForm
            request={latestStep.request}
            onSubmit={onSubmitProviderResponse}
          />
        )}
      </Stack>
    </ScrollArea>
  );
};

const StatusBullet = ({
  status,
  statusColor,
}: {
  status: ActionRequestStatus;
  statusColor: Record<ActionRequestStatus, string>;
}) => <Box w={8} h={8} bg={statusColor[status]} bdrs={99999} />;

const getFileKey = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}-${file.type}`;

const ProviderResponseForm = ({
  request,
  onSubmit,
}: {
  request: ActionRequest;
  onSubmit: ({
    request,
    message,
    files,
  }: {
    request: ActionRequest;
    message: string;
    files: FileWithPath[];
  }) => Promise<void>;
}) => {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit = Boolean(message.trim() || files.length);

  const handleDrop = (droppedFiles: FileWithPath[]) => {
    setSubmitError(null);
    setFiles((prev) => {
      const existing = new Map(prev.map((file) => [getFileKey(file), file]));
      droppedFiles.forEach((file) => existing.set(getFileKey(file), file));
      return Array.from(existing.values());
    });
  };

  const handleRemoveFile = (fileKey: string) => {
    setFiles((prev) => prev.filter((file) => getFileKey(file) !== fileKey));
  };

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await onSubmit({
        request,
        message,
        files,
      });
      setMessage('');
      setFiles([]);
    } catch {
      setSubmitError('Unable to submit response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      data-testid="provider-action-response-form"
    >
      <Stack gap="sm">
        <Textarea
          label="Response"
          placeholder="Add a comment for the billing team"
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
          data-testid="provider-action-response-message"
          autosize
          minRows={3}
        />
        <Dropzone
          onDrop={handleDrop}
          multiple
          data-testid="provider-action-response-dropzone"
        >
          <Group justify="center" gap="xs" mih={72}>
            <Dropzone.Accept>
              <IconUpload size={22} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={22} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPaperclip size={22} />
            </Dropzone.Idle>
            <Text size="sm">Attach files</Text>
          </Group>
        </Dropzone>
        {files.length > 0 && (
          <Stack gap={6} data-testid="provider-action-response-files">
            {files.map((file) => {
              const fileKey = getFileKey(file);
              return (
                <Group key={fileKey} justify="space-between" gap="xs">
                  <Group gap={6} style={{ minWidth: 0 }}>
                    <IconFile size={16} />
                    <Text size="sm" truncate>
                      {file.name}
                    </Text>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => handleRemoveFile(fileKey)}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              );
            })}
          </Stack>
        )}
        {submitError && (
          <Alert
            color="red"
            icon={<IconAlertTriangle size={16} />}
            data-testid="provider-action-response-error"
          >
            {submitError}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button
            leftSection={<IconSend size={16} />}
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            data-testid="provider-action-response-submit"
          >
            Send Response
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
};
